import cx from 'classnames'
import { useFocus } from '@react-aria/interactions'
import { forwardRef, useCallback, useLayoutEffect, useState, useEffect, ReactNode } from 'react'
import { InputContainerProps, InputProps } from 'almanac-ui/TextInputField/Types'
import Styles from './Input.module.scss'
import { mergeProps, useObjectRef } from '@react-aria/utils'

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = 'md',
      isValid = true,
      isFocused = false,
      placeholder,
      value,
      className,
      type,
      isTextarea,
      withBorder = true,
      scrollable,
      variant = 'default',
      style,
      ...rest
    },
    ref,
  ) => {
    return (
      <input
        ref={ref}
        {...rest}
        style={style}
        className={cx(
          Styles.input,
          Styles[size],
          Styles[variant],
          {
            [Styles.focused]: isFocused,
            [Styles.invalid]: !isValid,
            [Styles.withBorder]: withBorder,
          },
          className,
        )}
        type={type}
        value={value}
        placeholder={placeholder}
      />
    )
  },
)

const TextArea = forwardRef<HTMLTextAreaElement, InputProps>(
  (
    {
      size = 'md',
      isValid = true,
      isFocused = false,
      onInput: originalOnInput,
      placeholder,
      value,
      className,
      isTextarea,
      rows = 1,
      resizable = true,
      autoFocus,
      maxHeight,
      withBorder = true,
      scrollable = true,
      variant = 'default',
      ...rest
    },
    fRef,
  ) => {
    const textareaRef = useObjectRef(fRef)

    const onResize = useCallback(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = ''
        const height = maxHeight
          ? Math.min(
              maxHeight,
              textareaRef.current.scrollHeight + parseFloat(getComputedStyle(textareaRef.current).borderWidth) * 2,
            )
          : textareaRef.current.scrollHeight + parseFloat(getComputedStyle(textareaRef.current).borderWidth) * 2
        textareaRef.current.style.height = `${height}px`
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxHeight])

    // Set focus at the end of content
    useLayoutEffect(() => {
      if (textareaRef.current && autoFocus) {
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoFocus])

    // Resize on first render and whenever value changes (handles programmatic setValue)
    useLayoutEffect(() => {
      if (!resizable) return
      onResize()
    }, [onResize, resizable, value])

    useEffect(() => {
      setTimeout(() => {
        if (!resizable) return
        onResize()
      }, 10)
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <textarea
        {...rest}
        ref={textareaRef}
        rows={rows}
        onInput={(e) => {
          originalOnInput?.(e)
          // Resize on changes
          if (!resizable) return
          onResize()
        }}
        className={cx(
          Styles.input,
          Styles.textarea,
          Styles[size],
          Styles[variant],
          {
            [Styles.focused]: isFocused,
            [Styles.invalid]: !isValid,
            [Styles.withBorder]: withBorder,
            [Styles.disableScroll]: !scrollable,
          },
          className,
        )}
        value={value}
        placeholder={placeholder}
      />
    )
  },
)

const InputContainer = forwardRef<HTMLTextAreaElement | HTMLInputElement, InputContainerProps>(
  (
    {
      size = 'md',
      isTextarea = false,
      isValid = true,
      placeholder,
      value,
      className,
      errorMessage = (e) => e.validationErrors.join(' '),
      validMessage,
      withBorder = true,
      scrollable = true,
      errorMessageClassName,
      validationErrors,
      validationDetails,
      onFocusChange,
      doneOnEnter = false,
      ...rest
    },
    fRef,
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const { focusProps } = useFocus({
      onFocusChange: (isFocused) => {
        onFocusChange?.(isFocused)
        setIsFocused(isFocused)
      },
    })

    const ref = useObjectRef(fRef)

    const Element = isTextarea ? TextArea : Input
    let errorMessageString: ReactNode = null
    if (typeof errorMessage === 'function') {
      errorMessageString =
        !isValid && validationErrors != null && validationDetails != null
          ? errorMessage({
              isInvalid: true,
              validationErrors,
              validationDetails,
            })
          : null
    } else {
      errorMessageString = errorMessage
    }

    return (
      <>
        <Element
          enterKeyHint={doneOnEnter ? 'done' : undefined}
          onKeyDown={(e) => {
            if (doneOnEnter && e.key === 'Enter') {
              e.preventDefault()
              e.currentTarget.blur()
            } else {
              rest.onKeyDown?.(e)
            }
          }}
          isValid={isValid}
          placeholder={placeholder}
          value={value}
          size={size}
          isFocused={isFocused}
          className={className}
          withBorder={withBorder}
          scrollable={scrollable}
          ref={ref as any}
          {...mergeProps(focusProps, rest)}
        />
        {errorMessageString && (
          <span className={cx(Styles.errorMessage, errorMessageClassName)}>{errorMessageString}</span>
        )}
        {validMessage && <span className={Styles.validMessage}>{validMessage}</span>}
      </>
    )
  },
)

export { InputContainer as Input, TextArea }
